<template>
  <div class="product-option" :data-testid="option.code">
    <input
      type="radio"
      :name="product"
      :id="option.code"
      :value="option.code"
      :checked="selected"
      @change="optionSelected"
    />
    <label
      :for="option.code"
      class="product__information"
      :class="{ 'show-price': showPrice, checked: selected }"
    >
      <img
        class="product-option__img"
        v-if="option.image"
        :class="{ '--is-selected': selected }"
        :src="option.image"
        :alt="option.title"
      />
      <div class="image-spacer" v-else />
      <button
        type="button"
        class="product-option__info-btn"
        @click.stop="toggleInfoPanel"
      >
        ?
      </button>
      <h2
        class="product-option__title"
        :class="{ 'has-ribbon': !!option.ribbon }"
      >
        {{ option.title }}
      </h2>
      <div class="product-option__body" v-html="option.body" />
      <div class="product-option__price" v-if="showPrice">
        <span class="product-option__price-old" v-if="hasNonDiscountedPrice"
          >£{{ nonDiscountedPricePerPerson }}</span
        >
        <span class="product-option__price-current"
          >+£{{ pricePerPerson }}</span
        >
        <small class="pppw">
          per person per week
        </small>
      </div>
      <div v-if="showRadio" class="radio-button" />

      <span
        class="product-option__ribbon"
        :data-testid="'ribbon_' + option.code"
        v-if="option.ribbon"
        :style="{ backgroundColor: option.ribbon.colour }"
        >{{ option.ribbon.text }}</span
      >

      <ProductInfoOverlay
        v-if="infoPanelShowing"
        :title="option.title"
        :info="option.info"
        @close-info-panel="toggleInfoPanel"
      />
    </label>
  </div>
</template>

<script>
import ProductInfoOverlay from "@/components/ProductInfoOverlay";

export default {
  name: "Product",
  props: {
    product: String,
    option: Object,
    selected: Boolean,
    selectedPrice: Number,
    showPrice: Boolean,
    showRadio: Boolean,
  },
  data() {
    return {
      infoPanelShowing: false,
    };
  },
  computed: {
    nonDiscountedPricePerPerson() {
      return (this.option.price.old / 100).toFixed(2);
    },
    pricePerPerson() {
      return (this.option.price.current / 100).toFixed(2);
    },
    hasNonDiscountedPrice() {
      return !!this.option.price.old;
    },
  },
  methods: {
    toggleInfoPanel() {
      this.infoPanelShowing = !this.infoPanelShowing;
    },
    optionSelected(event) {
      this.$emit("option-selected", event.target.value);
      this.$emit("price-changed", this.option.price.current);
    },
  },
  created() {
    if (this.selected) {
      this.$emit("price-changed", this.option.price.current);
    }
  },
  components: {
    ProductInfoOverlay,
  },
};
</script>

<style lang="scss" scoped>
.product-option {
  position: relative;

  @include media("<medium") {
    margin-bottom: $spacing-medium;
  }

  @include media(">=medium") {
    flex-basis: 100%;
    margin: 0 5px 10px;
  }
}

input {
  @include visuallyhidden;
}

.product__information {
  position: relative;
  overflow: hidden;
  cursor: pointer;
  background-color: $white;
  border: 2px solid $white;
  border-radius: $border-radius-small;
  box-shadow: $brand-box-shadow;
  padding: $spacing-small;
  height: 100%;

  display: grid;
  grid-template-rows: auto min-content;
  grid-template-columns: min-content auto min-content;
  grid-auto-flow: column;

  &.show-price {
    grid-template-rows: min-content auto min-content;
  }

  input:checked + &,
  input:focus + & {
    border: 2px solid $brand-orange;
  }
}

.product-option__img {
  max-width: 65px;
  max-height: 45px;
  min-width: 30px;
  fill: currentColor;
  opacity: 0.7;
  filter: grayscale(100%);
  transition: opacity 0.2s linear, filter 0.4s linear;

  margin-bottom: $spacing-xs;
  grid-row: 1 / 2;
  justify-self: center;

  .show-price > & {
    grid-row: 1 / 3;
    margin-bottom: 0;
  }

  @include media("<medium") {
    max-width: 40px;
  }

  path {
    fill: currentColor;
  }

  &.--is-selected {
    fill: inherit;
    opacity: 1;
    filter: grayscale(0%);

    path {
      fill: inherit;
    }
  }
}

.product-option__info-btn {
  text-align: center;
  width: 17px;
  height: 17px;
  font-size: 1.2rem;
  font-weight: 500;
  color: $white;
  background-color: #afafaf;
  border: none;
  border-radius: 50%;
  padding: 0;
  margin: 0;

  justify-self: center;
  align-self: center;
  grid-row-start: 2;

  .show-price > & {
    grid-row-start: 3;
  }
}

.product-option__title {
  font-size: $button-font-size;
  text-align: initial;
  font-weight: $font-medium;
  margin: 0 0 0 $spacing-xs;

  grid-column: 2 / 4;

  &.has-ribbon {
    padding-right: 2rem;
  }
}

.product-option__body {
  font-size: 1.6rem;
  margin-left: $spacing-xs;

  align-self: center;

  .show-price {
    grid-column: 2 / 4;
  }

  * {
    margin-top: 0;
  }

  :first-child {
    margin-top: 0;
  }

  &:last-child,
  :last-child {
    margin-bottom: 0;
  }

  ::v-deep ul,
  ::v-deep ol {
    padding: 0 0 0 19px;
    margin: 0;
  }
}

.product-option__price {
  margin-left: $spacing-xs;

  &-old {
    color: #afafaf;
    text-decoration: line-through;
    font-size: $small-font-size;
    font-weight: $font-bold;
  }

  &-current {
    font-weight: bold;
    font-size: $button-font-size;
  }

  .pppw {
    font-weight: $font-light;
    font-size: $subtitle-font-size;
  }
}

.product-option__ribbon {
  position: absolute;
  top: 15px;
  right: -26px;
  width: 100px;
  text-align: center;
  text-transform: uppercase;
  font-size: 1.1rem;
  color: $white;
  background-color: $grey-dark;
  transform: rotate(45deg);
  padding: 3px 5px;
}

.radio-button {
  min-width: 22px;
  min-height: 22px;
  max-width: 22px;
  max-height: 22px;
  background-color: $white;
  border: 2px solid $grey-mid;
  border-radius: 50%;
  box-shadow: inset 0 0 0 3px $white;
  margin-bottom: 3px;

  .checked > & {
    background-color: $brand-green;
  }

  justify-self: center;
  align-self: end;
  grid-row-start: 2;

  .show-price > & {
    grid-row-start: 3;
  }
}

.image-spacer {
  min-width: 30px;
  grid-row: 1 / 2;

  .show-price > & {
    grid-row: 1 / 3;
  }
}
</style>
